import React, { useEffect, useState } from "react";
import { Table, Row, Spin } from "antd";
import { get, sortBy, toLower, toString } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  buildQueryString,
  checkRoles,
  exportFile,
  formatNumber,
  showError,
  showSuccess,
} from "../../../utils/utilities";
import { setFilters } from "../../../redux/ducks/Filters";
import Filter from "../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { dbDate, langMonth, langDate, timesheetFilterRoles, paginationDefaults } from "../../../utils/properties";
import { DownloadOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import TsReport from "../../../services/api/TsReport";
import { setLoading } from "../../../redux/ducks/Loading";
import TimesheetPortal from "../../../services/api/TimesheetPortal";
import dayjs from "dayjs";

const TimesheetReport = () => {
  const { t } = useTranslation();
  const userRoles = useSelector((state) => get(state.userInfoDuck, "userInfo.roles", {}));
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const currentDate = dayjs().format("YYYYMM");
  const prevMonth = dayjs().subtract(1, "month").endOf("month").format("YYYYMM");
  const [state, setState] = useState({
    dataContent: [],
    employee: [],
    services: [],
    projects: [],
    projectsActivities: [],
    loading: false,
    downloadLoading: false,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.total,
    },
  });
  const statusOrder = ["REFUTED", "WAITING_APPROVAL", "CREATED", "APPROVED"];

  useEffect(() => {
    const queryString = buildQueryString(
      {},
      {
        startMonth: prevMonth,
        endMonth: currentDate,
        currentPage: paginationDefaults.page,
        size: paginationDefaults.size,
        totalItems: paginationDefaults.total,
      }
    );
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    if (checkRoles(userRoles, timesheetFilterRoles.user)) {
      getEmployee();
    }
    if (checkRoles(userRoles, timesheetFilterRoles.projectActivity)) {
      getProjects();
    }
    if (checkRoles(userRoles, timesheetFilterRoles.service)) {
      getServices();
    }
  }, []);

  const dataList = async (queryString) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const report = await TsReport.getTimesheetList(queryString);
    if (!report.error && report.data) {
      report.data.forEach((cc, i) => {
        cc.key = i;
        cc.project = `${cc.projectId} | ${cc.projectName}`;
        cc.projectTask = `${cc.projectTaskId} | ${cc.projectTaskName}`;
      });
      const sortedReportData = sortBy(report.data, (item) => {
        return statusOrder.indexOf(item.approvalStatus);
      });
      setState((prevState) => ({
        ...prevState,
        dataContent: sortedReportData,
        pagination: {
          currentPage: report.page ? report.page + 1 : paginationDefaults.page,
          size: get(report, "size", paginationDefaults.size),
          totalItems: get(report, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(report, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const getServices = async () => {
    const services = [];
    const serviceData = await TsReport.getServices();
    if (!serviceData.error && serviceData.data) {
      serviceData.data.forEach((cc) => services.push({ value: cc.id, label: cc.name }));
      setState((prevState) => ({ ...prevState, services }));
    } else {
      showError(get(serviceData, "error.messageCode", "ER000"));
    }
  };

  const getProjects = async () => {
    const projects = [];
    const projectsData = await TsReport.getProject();
    if (!projectsData.error && projectsData.data) {
      projectsData.data.forEach((cc) => projects.push({ value: cc.id, label: cc.name }));
      setState((prevState) => ({ ...prevState, projects }));
    } else {
      showError(get(projectsData, "error.messageCode", "ER000"));
    }
  };

  const getEmployee = async () => {
    const employee = [];
    const employeeData = await TsReport.getEmployee();
    if (!employeeData.error && employeeData.data) {
      employeeData.data.forEach((cc, i) =>
        employee.push({ key: i, value: cc.cid, label: `${cc.name} ${cc.surname}`, ...cc })
      );
      setState((prevState) => ({ ...prevState, employee }));
    } else {
      showError(get(employeeData, "error.messageCode", "ER000"));
    }
  };

  const getProjectsActivities = async (id) => {
    dispatch(setLoading(true));
    const projectsActivities = [];
    const projectsData = await TsReport.getProjectTasks(`projectId=${id}`);
    if (!projectsData.error && projectsData.data) {
      projectsData.data.forEach((cc) => projectsActivities.push({ value: cc.id, label: cc.name }));
      setState((prevState) => ({ ...prevState, projectsActivities }));
    } else {
      showError(get(projectsData, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = [
    {
      title: t("timesheet.report.table.data"),
      dataIndex: "date",
      key: "date",
      render: (date) => date !== null && dayjs(date, dbDate).isValid() && dayjs(date, dbDate).format(dateFormat),
    },
    {
      title: t("timesheet.report.table.employee"),
      dataIndex: "employeeName",
      key: "employeeName",
    },
    {
      title: t("timesheet.report.table.project"),
      dataIndex: "project",
      key: "project",
    },
    {
      title: t("timesheet.report.table.activity"),
      dataIndex: "projectTask",
      key: "projectTask",
    },
    {
      title: t("timesheet.report.table.service"),
      dataIndex: "serviceId",
      key: "serviceId",
    },
    {
      title: t("timesheet.report.table.hours"),
      dataIndex: "hours",
      key: "hours",
      className: "table-value-right",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.report.table.houndredthsOfHours"),
      dataIndex: "hundredthsOfHours",
      key: "hundredthsOfHours",
      className: "table-value-right",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.report.table.status"),
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      render: (val) => {
        switch (val) {
          case "CREATED":
            return t("timesheet.created");
          case "APPROVED":
            return t("timesheet.approved");
          case "REFUTED":
            return t("timesheet.refuted");
          case "WAITING_APPROVAL":
            return t("timesheet.waitingApproval");
          default:
            break;
        }
      },
    },
    {
      title: t("timesheet.report.table.lastModifierName"),
      dataIndex: "lastModifierName",
      key: "lastModifierName",
    },
    {
      title: t("timesheet.report.table.lastApprovalerName"),
      dataIndex: "lastApprovalerName",
      key: "lastApprovalerName",
    },
  ];

  const exportReportCsv = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await TimesheetPortal.downloadTimesheetReport(filters.string);
    if (!exportResponse.error) {
      showSuccess();
      const blob = new Blob([exportResponse]);
      exportFile(blob, "report.csv");
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const resetFilter = () => {
    const queryString = buildQueryString(
      {},
      {
        startMonth: prevMonth,
        endMonth: currentDate,
        currentPage: paginationDefaults.page,
        size: paginationDefaults.size,
        totalItems: paginationDefaults.total,
      }
    );
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    setState((prevState) => ({ ...prevState, projectsActivities: [] }));
  };

  const formikFilter = useFormik({
    initialValues: {
      rangeMonth: [dayjs(prevMonth, "YYYYMM"), dayjs(currentDate, "YYYYMM")],
      employee: null,
      project: null,
      activity: null,
      service: null,
    },
    enableReinitialize: true,
    validate: (values) => {
      let errors = {};
      if (!values.rangeMonth) errors.rangeMonth = "Required!";
      return errors;
    },
    onSubmit: (values) => {
      const submit = {
        startMonth: dayjs(values.rangeMonth[0], "YYYYMM").format("YYYYMM"),
        endMonth: dayjs(values.rangeMonth[1], "YYYYMM").format("YYYYMM"),
        employeeCid: values.employee,
        projectId: values.project,
        projectTaskId: values.activity,
        serviceId: values.service,
      };
      const queryString = buildQueryString(filters.obj, submit);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "rangeMonth",
      isRequired: true,
      id: "rangeMonth",
      name: "rangeMonth",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("report.filter.rangeMonth"),
      value: formikFilter.values.rangeMonth,
      onChange: (date) => {
        formikFilter.setFieldValue("rangeMonth", date);
      },
      hidden: !checkRoles(userRoles, timesheetFilterRoles.date),
    },
    {
      type: "selectWithSearch",
      allowClear: true,
      isRequired: false,
      id: "employee",
      name: "employee",
      value: formikFilter.values.employee,
      label: t("timesheet.report.filter.employee"),
      placeholder: t("timesheet.report.filter.employee"),
      onBlur: formikFilter.handleBlur("employee"),
      dataSelect: get(state, "employee", []),
      onChange: (value) => {
        formikFilter.setFieldValue("employee", value);
      },
      hidden: !checkRoles(userRoles, timesheetFilterRoles.user),
      filterOption: true,
      optionFilterProp: "label",
    },
    {
      type: "select",
      allowClear: true,
      isRequired: false,
      id: "project",
      name: "project",
      value: formikFilter.values.project,
      label: t("timesheet.report.filter.project"),
      onBlur: formikFilter.handleBlur("project"),
      dataSelect: get(state, "projects", []),
      onChange: (value) => {
        formikFilter.setFieldValue("project", value);
        getProjectsActivities(value);
      },
      hidden: !checkRoles(userRoles, timesheetFilterRoles.projectActivity),
    },
    {
      type: "select",
      allowClear: true,
      isRequired: false,
      id: "activity",
      name: "activity",
      value: formikFilter.values.activity,
      label: t("timesheet.report.filter.activity"),
      onBlur: formikFilter.handleBlur("activity"),
      dataSelect: get(state, "projectsActivities", []),
      onChange: (value) => {
        formikFilter.setFieldValue("activity", value);
      },
      hidden: !checkRoles(userRoles, timesheetFilterRoles.projectActivity),
    },
    {
      type: "select",
      allowClear: true,
      isRequired: false,
      id: "service",
      name: "service",
      value: formikFilter.values.service,
      label: t("timesheet.report.filter.service"),
      onBlur: formikFilter.handleBlur("service"),
      dataSelect: get(state, "services", []),
      onChange: (value) => {
        formikFilter.setFieldValue("service", value);
      },
      hidden: !checkRoles(userRoles, timesheetFilterRoles.service),
    },
  ];

  return (
    <>
      {state.loading ? (
        <div className="container-spinner-positioning">
          <Spin spinning={state.loading} className="spinner-positioning" />
        </div>
      ) : (
        ""
      )}
      <Filter
        formikForm={formikFilter}
        fields={fieldsFilter}
        onReset={resetFilter}
        isReportTimesheetPm={
          !checkRoles(userRoles, ["SYSTEM_IT", "CONTROLLER"]) &&
          checkRoles(userRoles, ["PM", "SUPER_PM", "SUPPORT_PM", "TIMESHEET_SUBSTITUTE"])
        } // Necessary for the position of buttons of filter
      />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={exportReportCsv}
        />
      </Row>
      <Table
        dataSource={state.dataContent}
        columns={columns}
        pagination={{
          currentPage: state.pagination.page,
          size: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, size) => {
            const pagination = state.pagination;
            pagination.page = toString(page - 1);
            pagination.size = size;
            const queryString = buildQueryString(filters.obj, pagination);
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: size,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
        scroll={true}
      />
    </>
  );
};

export default TimesheetReport;
